import { Icon } from "@legal-500/legal500-design-system";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Typeahead.module.css";

export type TypeaheadProps<T> = {
  value: T | null;
  onChange: (value: T | null) => void;
  options: T[];
  getLabel: (item: T) => string;
  getKey?: (item: T) => string;
  getSublabel?: (item: T) => string;
  placeholder?: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  /**
   * When true, render a required marker (`*`) on the label and set
   * `aria-required="true"` on the combobox input. Mirrors the design-system
   * `Input` / `DatePicker` `required` convention.
   */
  required?: boolean;
  onCreateNew?: (query: string) => void;
  createLabel?: string;
  emptyMessage?: string;
  /**
   * Called with the raw typed term whenever the input changes (and when the
   * query is cleared). Optional — lets a parent observe the search term so it
   * can drive a server-side query. The internal local filter over `options`
   * still applies as a refinement over whatever page the parent returns.
   */
  onInputChange?: (query: string) => void;
  /**
   * When true and the menu is open, render a "Searching…" status row in place
   * of the options/empty message and suppress the create affordance. Lets a
   * parent signal that a debounced server-side search is in flight.
   */
  loading?: boolean;
};

export function Typeahead<T>({
  value,
  onChange,
  options,
  getLabel,
  getKey,
  getSublabel,
  placeholder = "Search…",
  label,
  id,
  disabled = false,
  required = false,
  onCreateNew,
  createLabel = "Create new",
  emptyMessage = "No matches",
  onInputChange,
  loading = false,
}: TypeaheadProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [placement, setPlacement] = useState<"below" | "above">("below");

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const fieldId =
    id ?? (label ? `typeahead-${label.toLowerCase().replace(/\s+/g, "-")}` : undefined);

  const keyFor = (item: T, index: number): string =>
    getKey ? getKey(item) : `${index}-${getLabel(item)}`;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(opt => {
      const label = getLabel(opt).toLowerCase();
      if (label.includes(q)) return true;
      const sub = getSublabel?.(opt).toLowerCase();
      return sub ? sub.includes(q) : false;
    });
  }, [options, query, getLabel, getSublabel]);

  const hasExactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return false;
    return options.some(opt => getLabel(opt).toLowerCase() === q);
  }, [options, query, getLabel]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
        onInputChange?.("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onInputChange]);

  // Decide whether to render the menu above or below the input. We measure the
  // menu's actual height once it mounts; if there isn't room below the input
  // but there is above, flip. Re-evaluate on scroll, resize, and any change in
  // the menu's own size (filter results growing/shrinking).
  useEffect(() => {
    if (!open) return;
    const inputEl = inputRef.current;
    const menuEl = menuRef.current;
    if (!inputEl || !menuEl) return;

    const compute = () => {
      const inputRect = inputEl.getBoundingClientRect();
      const menuHeight = menuEl.offsetHeight;
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      const gutter = 8;
      setPlacement(spaceBelow < menuHeight + gutter && spaceAbove > spaceBelow ? "above" : "below");
    };
    compute();
    // ResizeObserver isn't available in older test environments (JSDOM).
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(compute) : null;
    resizeObserver?.observe(menuEl);
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  const displayValue = open ? query : value ? getLabel(value) : "";

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    setHighlight(0);
  };

  // Reset the input's search term and let any parent observer reset its own
  // (server-side) query to the unfiltered page.
  const resetQuery = () => {
    setQuery("");
    onInputChange?.("");
  };

  const handleSelect = (option: T) => {
    onChange(option);
    resetQuery();
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleCreate = () => {
    if (!onCreateNew) return;
    onCreateNew(query.trim());
    resetQuery();
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    resetQuery();
    inputRef.current?.focus();
  };

  const showCreate = !loading && !!onCreateNew && query.trim().length > 0 && !hasExactMatch;
  const totalCount = filtered.length + (showCreate ? 1 : 0);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        openMenu();
      } else {
        setHighlight(h => Math.min(h + 1, Math.max(totalCount - 1, 0)));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      if (highlight < filtered.length) {
        const picked = filtered[highlight];
        if (picked !== undefined) handleSelect(picked);
      } else if (showCreate) {
        handleCreate();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      resetQuery();
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className={styles.root}>
      {label && (
        <label htmlFor={fieldId} className={styles.label}>
          {label}
          {required && (
            <span className={styles.requiredIndicator} aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </label>
      )}
      <div className={styles.control}>
        <input
          id={fieldId}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-required={required ? "true" : undefined}
          aria-controls={fieldId ? `${fieldId}-listbox` : undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue}
          className={styles.input}
          onFocus={openMenu}
          onClick={openMenu}
          onChange={e => {
            setQuery(e.target.value);
            onInputChange?.(e.target.value);
            setHighlight(0);
            if (!open) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <div className={styles.adornments}>
          {!disabled && value && (
            <button
              type="button"
              className={styles.iconButton}
              aria-label="Clear"
              onClick={handleClear}
            >
              <Icon icon="cross" size="small" />
            </button>
          )}
          <button
            type="button"
            className={styles.iconButton}
            tabIndex={-1}
            aria-label="Toggle options"
            disabled={disabled}
            onClick={() => (open ? setOpen(false) : openMenu())}
          >
            <Icon icon={open ? "chevron-up" : "chevron-down"} size="small" />
          </button>
        </div>

        {open && (
          <div
            ref={menuRef}
            className={clsx(styles.menu, placement === "above" && styles.menuAbove)}
            id={fieldId ? `${fieldId}-listbox` : undefined}
            role="listbox"
          >
            {loading ? (
              <div className={styles.status} role="status" aria-live="polite">
                <span className={styles.spinner} aria-hidden="true" />
                Searching…
              </div>
            ) : filtered.length === 0 && !showCreate ? (
              <div className={styles.empty}>{emptyMessage}</div>
            ) : (
              filtered.map((opt, idx) => {
                const isHighlighted = idx === highlight;
                const isSelected = value !== null && getLabel(opt) === getLabel(value);
                const sub = getSublabel?.(opt);
                return (
                  <button
                    key={keyFor(opt, idx)}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={clsx(
                      styles.option,
                      isHighlighted && styles.optionHighlighted,
                      isSelected && styles.optionSelected,
                    )}
                    onMouseDown={e => {
                      e.preventDefault();
                      handleSelect(opt);
                    }}
                    onMouseEnter={() => setHighlight(idx)}
                  >
                    <span className={styles.optionLabel}>{getLabel(opt)}</span>
                    {sub && <span className={styles.optionSublabel}>{sub}</span>}
                  </button>
                );
              })
            )}
            {showCreate && (
              <button
                type="button"
                className={clsx(
                  styles.createOption,
                  highlight === filtered.length && styles.optionHighlighted,
                )}
                onMouseDown={e => {
                  e.preventDefault();
                  handleCreate();
                }}
                onMouseEnter={() => setHighlight(filtered.length)}
              >
                <Icon icon="plus" size="small" />
                <span>{createLabel}</span>
                <span className={styles.createQuery}>&quot;{query.trim()}&quot;</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
